import React from "react";
import { Controller } from "react-hook-form";

import { SegmentedControl } from "../../../../components/MobileForm";
import {
  Card,
  CardLabel,
  HelperText,
} from "../../../../components/MobileForm/styles";
import { FormInput } from "../../../../components/Input";
import { AccountMultiSelect } from "../../../../components/AccountMultiSelect";
import { ReturnSlider } from "../../../../components/ReturnSlider";
import { CADENCE_OPTIONS } from "./constants";
import { PredictionDate, PredictionRow } from "./styles";
import { MobileInvestmentGoalFormProps } from "./types";

export const MobileInvestmentGoalForm: React.FC<
  MobileInvestmentGoalFormProps
> = ({ control, accounts, predictionLabel, isSubmitting }) => {
  return (
    <>
      <Card>
        <CardLabel htmlFor="mobile-investment-goal-name">Goal name *</CardLabel>
        <FormInput
          id="mobile-investment-goal-name"
          required
          name="name"
          control={control}
          rules={{
            required: "Name is required!",
            minLength: {
              value: 3,
              message: "Name should have more than 3 letters!",
            },
          }}
          handleClear={false}
          disabled={isSubmitting}
          placeholder="e.g. Retirement"
          autoFocus={true}
          variant="title"
        />

        <CardLabel htmlFor="mobile-investment-goal-target-amount">
          Target amount (€) *
        </CardLabel>
        <FormInput
          id="mobile-investment-goal-target-amount"
          required
          name="targetAmount"
          control={control}
          type="number"
          rules={{
            required: "Target amount is required!",
            min: { value: 1, message: "Target must be greater than 0!" },
          }}
          handleClear={false}
          disabled={isSubmitting}
          placeholder="e.g. 100000"
        />
      </Card>

      <Card>
        <CardLabel>Assumed annual return</CardLabel>
        <Controller
          name="assumedAnnualReturn"
          control={control}
          render={({ field: { value, onChange } }) => (
            <ReturnSlider
              value={value}
              onChange={onChange}
              disabled={isSubmitting}
            />
          )}
        />
        <PredictionRow>
          <span>Estimated achievement</span>
          <PredictionDate>{predictionLabel}</PredictionDate>
        </PredictionRow>
        <HelperText>Projections are estimates, not guarantees.</HelperText>

        <CardLabel htmlFor="mobile-investment-goal-contribution">
          Contribution (€ / period) *
        </CardLabel>
        <FormInput
          id="mobile-investment-goal-contribution"
          required
          name="contributionRate"
          control={control}
          type="number"
          rules={{
            required: "Contribution is required!",
            min: { value: 0, message: "Contribution cannot be negative!" },
          }}
          handleClear={false}
          disabled={isSubmitting}
          placeholder="e.g. 500"
        />

        <CardLabel>Contribution cadence *</CardLabel>
        <Controller
          name="contributionCadence"
          control={control}
          rules={{ required: "Cadence is required!" }}
          render={({ field }) => (
            <SegmentedControl
              options={CADENCE_OPTIONS}
              selectedValue={field.value?.value ?? ""}
              disabled={isSubmitting}
              onChange={(value) => {
                const option = CADENCE_OPTIONS.find(
                  (cadence) => cadence.value === value,
                );
                field.onChange(option);
              }}
            />
          )}
        />
      </Card>

      <Card>
        <CardLabel>Linked investment accounts *</CardLabel>
        <Controller
          name="accountIds"
          control={control}
          rules={{
            validate: (value: number[]) =>
              value.length > 0 || "Link at least one account!",
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <>
              <AccountMultiSelect
                accounts={accounts}
                value={value}
                onChange={onChange}
                disabled={isSubmitting}
                emptyLabel="No investment accounts available."
              />
              {error && <HelperText>{error.message}</HelperText>}
            </>
          )}
        />
        <HelperText>Current value is measured from these accounts.</HelperText>
      </Card>
    </>
  );
};
